import React from 'react';
import { isNotEmpty } from '@intelligence/v3/utils/common';
import { getNodePlainText } from './dom';

export interface ISearchableSelectOption {
  searchText?: string;
  text?: React.ReactNode;
  value?: React.ReactNode;
  buttonText?: React.ReactNode;
  tooltip?: React.ReactNode;
}

/**
 * 获取下拉选项的搜索匹配文本。
 *
 * 会按优先字段收集选项中的可搜索内容：
 * `searchText`、`text`、`buttonText`，
 * 并通过 `getNodePlainText` 兼容字符串、数字、ReactNode 等不同形态。
 *
 * 返回值统一转为小写，方便后续进行大小写不敏感匹配。
 *
 * @param option 下拉选项配置
 * @returns 用于搜索匹配的归一化文本
 */
export const getOptionSearchText = (option?: ISearchableSelectOption | null): string => {
  if (!option) {
    return '';
  }

  return [option?.searchText, option?.text, option?.buttonText]
    .map(getNodePlainText)
    .filter(isNotEmpty)
    .join(' ')
    .toLowerCase();
};

/**
 * 默认下拉选项搜索逻辑。
 *
 * 处理规则：
 * - 搜索关键字会去除首尾空格并转为小写
 * - 空关键字默认不过滤，返回 `true`
 * - 非空关键字会与 `getOptionSearchText(option)` 的结果做 `includes` 匹配
 *
 * @param inputValue 用户输入的搜索关键字
 * @param option 当前待匹配的下拉选项
 * @returns 当前选项是否命中搜索关键字
 */
export const defaultFilter = (inputValue?: string, option?: ISearchableSelectOption | null): boolean => {
  const normalizedInputValue = String(inputValue || '')
    .trim()
    .toLowerCase();

  if (!normalizedInputValue) {
    return true;
  }

  return getOptionSearchText(option).includes(normalizedInputValue);
};
